import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Monitor, ScanRun, Opportunity, MonitorPlatform } from "../../../shared/types";

// ── Response shapes ──

interface MonitorWithScan extends Monitor {
  latest_scan: ScanRun | null;
}

interface MonitorListResponse {
  data: Monitor[];
}

interface MonitorDetailResponse {
  data: MonitorWithScan;
}

interface ScanTriggerResponse {
  data: { scan_run_id: string; status: string; keywords_count: number };
}

interface OpportunityListResponse {
  data: Opportunity[];
}

interface OpportunityDetailResponse {
  data: Opportunity & { full_thread: unknown };
}

interface ScanListResponse {
  data: ScanRun[];
}

interface KeywordSuggestResponse {
  data: {
    pain_point_keywords: string[];
    competitor_keywords: string[];
    feature_keywords: string[];
    category_keywords: string[];
    all: string[];
  };
}

// ── Queries ──

export function useMonitors(brandId: string | undefined) {
  return useQuery({
    queryKey: ["monitors", brandId],
    queryFn: () => api.get<MonitorListResponse>(`/api/monitors?brand_id=${brandId}`),
    enabled: !!brandId,
  });
}

export function useMonitor(monitorId: string | undefined) {
  return useQuery({
    queryKey: ["monitors", "detail", monitorId],
    queryFn: () => api.get<MonitorDetailResponse>(`/api/monitors/${monitorId}`),
    enabled: !!monitorId,
  });
}

export function useOpportunities(
  monitorId: string | undefined,
  filters?: { status?: string; platform?: MonitorPlatform; min_score?: number; sort?: string },
) {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.min_score) params.set("min_score", String(filters.min_score));
  if (filters?.sort) params.set("sort", filters.sort);
  const qs = params.toString();

  return useQuery({
    queryKey: ["opportunities", monitorId, filters],
    queryFn: () =>
      api.get<OpportunityListResponse>(`/api/monitors/${monitorId}/opportunities${qs ? `?${qs}` : ""}`),
    enabled: !!monitorId,
  });
}

export function useOpportunity(monitorId: string | undefined, oppId: string | undefined) {
  return useQuery({
    queryKey: ["opportunities", monitorId, oppId],
    queryFn: () =>
      api.get<OpportunityDetailResponse>(`/api/monitors/${monitorId}/opportunities/${oppId}`),
    enabled: !!monitorId && !!oppId,
  });
}

export function useScanHistory(monitorId: string | undefined) {
  return useQuery({
    queryKey: ["scans", monitorId],
    queryFn: () => api.get<ScanListResponse>(`/api/monitors/${monitorId}/scans`),
    enabled: !!monitorId,
  });
}

export function useKeywordSuggestions(monitorId: string | undefined) {
  return useQuery({
    queryKey: ["keywords", monitorId],
    queryFn: () => api.get<KeywordSuggestResponse>(`/api/monitors/${monitorId}/keywords/suggest`),
    enabled: !!monitorId,
  });
}

// ── Mutations ──

export function useCreateMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      brand_id: string;
      platforms?: MonitorPlatform[];
      keywords?: string[];
      competitor_names?: string[];
      scan_frequency?: string;
    }) => api.post<{ data: Monitor }>("/api/monitors", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monitors"] });
    },
  });
}

export function useUpdateMonitor(monitorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Monitor>) =>
      api.put<{ data: Monitor }>(`/api/monitors/${monitorId}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monitors"] });
    },
  });
}

export function useTriggerScan(monitorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ScanTriggerResponse>(`/api/monitors/${monitorId}/scan`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scans", monitorId] });
      // Poll for opportunities after a delay
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["opportunities", monitorId] });
        qc.invalidateQueries({ queryKey: ["monitors", "detail", monitorId] });
      }, 5000);
    },
  });
}

export function useUpdateOpportunity(monitorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ oppId, ...payload }: { oppId: string; status?: string; draft_response?: string; skip_reason?: string; posted_url?: string }) =>
      api.put<{ data: Opportunity }>(`/api/monitors/${monitorId}/opportunities/${oppId}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities", monitorId] });
    },
  });
}

export function useRedraftOpportunity(monitorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (oppId: string) =>
      api.post<{ data: { draft_response: string } }>(`/api/monitors/${monitorId}/opportunities/${oppId}/redraft`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities", monitorId] });
    },
  });
}
